import { lotOwners, resolveLot, type LotClaim } from '../net/lots';
import type { Neighbor } from '../net/Session';
import { DecorManager } from '../world/DecorManager';
import { HOME_LOTS, homeLot, type HomeKind } from '../world/home';
import { trailerStyle, type HomeLot } from '../world/HomeLot';
import type { World } from '../world/World';

/** Lot 7 first, then Possum Trot (east side, nearest Main Street, before the west side). */
export const LOT_PREFERENCE: readonly number[] = HOME_LOTS.map((l) => l.index);

interface Placed {
  lot: number;
  kind: HomeKind;
  home: HomeLot;
  decor: DecorManager;
  decorKey: string;
}

export interface HomeHooks {
  /** Your own claim: the lot you're on, whether you host, when you joined. */
  myClaim(): LotClaim;
  /** Someone outranks you for your lot: pack up and move. */
  moveMyHome(index: number): void;
}

/**
 * Everybody in a multiplayer park gets a lot of their own. This places neighbors' homes
 * (their trailer, yard decor and Camaro) on their lots, and moves yours when somebody
 * with dibs (the park's host, or an earlier arrival) has claimed your lot.
 */
export class NeighborHomes {
  private readonly placed = new Map<string, Placed>();

  constructor(
    private readonly world: World,
    private readonly hooks: HomeHooks,
  ) {}

  /** Call whenever anyone's claim (or the neighbor list) changes. Returns your lot. */
  sync(neighbors: readonly Neighbor[]): number {
    const others: LotClaim[] = neighbors.map((n) => ({ id: n.id, lot: n.lot, host: n.host, since: n.since }));
    const me = this.hooks.myClaim();
    const mine = resolveLot(me, others, LOT_PREFERENCE);
    if (mine !== me.lot) this.hooks.moveMyHome(mine);

    const owners = lotOwners(others);
    const keep = new Set<string>();
    for (const n of neighbors) {
      if (owners.get(n.lot)?.id !== n.id || n.lot === mine) continue;
      keep.add(n.id);
      const kind: HomeKind = n.status?.home === 1 ? 'double' : 'single';
      const p = this.placed.get(n.id);
      if (p && p.lot === n.lot && p.kind === kind) continue;
      if (p) this.remove(n.id);
      const frame = homeLot(n.lot);
      const home = this.world.placeHome(frame, trailerStyle(n.look), n.name.slice(0, 12), kind);
      const decor = new DecorManager(home, this.world.dynamic, this.world.collision, frame, kind);
      this.placed.set(n.id, { lot: n.lot, kind, home, decor, decorKey: '' });
    }
    for (const id of [...this.placed.keys()]) if (!keep.has(id)) this.remove(id);
    return mine;
  }

  /** Homes that are up right now: whose, on which lot, and what kind. */
  placedHomes(): Array<{ id: string; lot: number; kind: HomeKind }> {
    return [...this.placed].map(([id, p]) => ({ id, lot: p.lot, kind: p.kind }));
  }

  /** Keeps yards and driveways current: decor bought, Camaro fixed, out for a drive. */
  update(
    neighbors: readonly Neighbor[],
    driving: (id: string) => boolean,
    time: number,
    dt: number,
    night: number,
  ): void {
    let rebuild = false;
    for (const n of neighbors) {
      const p = this.placed.get(n.id);
      if (!p) continue;
      const status = n.status;
      // They bought the double wide: it gets delivered.
      if ((status?.home === 1 ? 'double' : 'single') !== p.kind) {
        rebuild = true;
        continue;
      }
      if (status) {
        const key = status.decor.join();
        if (key !== p.decorKey) {
          p.decorKey = key;
          p.decor.sync(status.decor);
          p.decor.setGooseOutfit(status.day);
        }
        const camaro = status.ride === 'camaro';
        p.home.setParkedCar(
          driving(n.id) || (camaro && status.car === 2)
            ? 'none'
            : !camaro || status.car === 1
              ? 'fixed'
              : 'blocks',
          status.ride,
        );
      } else p.home.setParkedCar('blocks');
      p.decor.update(time, dt, night);
    }
    if (rebuild) this.sync(neighbors);
  }

  clear(): void {
    for (const id of [...this.placed.keys()]) this.remove(id);
  }

  private remove(id: string): void {
    const p = this.placed.get(id);
    if (!p) return;
    p.decor.clear();
    // Only tear down the home if it's still the one we built there.
    if (this.world.homes.get(p.lot) === p.home) this.world.removeHome(p.lot);
    this.placed.delete(id);
  }
}
